import type { FormEvent } from "react";
import { useEffect, useState } from "react";
import { normalizeStudentNamePart, type FamilyAccountsResponseDto } from "@madamuh/domain";
import { useNavigate, useParams } from "react-router-dom";
import { createAdminStudent, fetchFamilyAccounts } from "../../shared/api/adminRoster";
import { GENERIC_TENANT_LABELS, getTenantLabels } from "../../shared/lib/tenantLabels";
import { getProtectedApiErrorMessage, isUnauthenticatedApiError } from "../../shared/session/protectedApiError";
import { useProtectedRouteSession } from "../../shared/session/useProtectedRouteSession";
import { OperatorPageScaffold } from "../../shared/ui/protected/OperatorPageScaffold";
import { buildAdminRosterLinks } from "./AdminRosterListShared";
import {
  buildCreateStudentRequest,
  createInitialStudentFormState,
  StudentCreateForm,
  syncFamilyAccountNameFromStudentFamilyName,
  type StudentFormState
} from "./AdminRosterForms";

export function AdminStudentCreatePage() {
  const navigate = useNavigate();
  const { tenantSlug = "unknown-tenant" } = useParams();
  const { routeStatus, sessionStatus, session, errorMessage, isSessionReady } = useProtectedRouteSession(tenantSlug);
  const [familyAccounts, setFamilyAccounts] = useState<FamilyAccountsResponseDto | null>(null);
  const [studentForm, setStudentForm] = useState<StudentFormState>(createInitialStudentFormState());
  const [hasEditedCreateFamilyAccountDisplayName, setHasEditedCreateFamilyAccountDisplayName] = useState(false);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "error">("idle");
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const hasAdminRole = Boolean(session?.member?.roles.includes("admin"));
  const tenantLabels = session ? getTenantLabels(session.tenant.uiLabelProfile) : GENERIC_TENANT_LABELS;

  useEffect(() => {
    if (!isSessionReady || !hasAdminRole) return;
    setLoadState("loading");
    fetchFamilyAccounts(tenantSlug)
      .then((nextFamilyAccounts) => {
        setFamilyAccounts(nextFamilyAccounts);
        setLoadState("idle");
      })
      .catch((error) => {
        if (isUnauthenticatedApiError(error)) {
          navigate(`/t/${tenantSlug}/entry`, { replace: true });
          return;
        }
        setLoadState("error");
        setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.familyAccount}の取得に失敗しました`));
      });
  }, [hasAdminRole, isSessionReady, navigate, tenantLabels.familyAccount, tenantSlug]);

  useEffect(() => {
    setStudentForm((current) => syncFamilyAccountNameFromStudentFamilyName(current, hasEditedCreateFamilyAccountDisplayName));
  }, [hasEditedCreateFamilyAccountDisplayName, studentForm.familyName, studentForm.familyAccountMode]);

  async function handleCreateStudent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setLoadError(null);
    setFeedbackMessage(null);

    if (!normalizeStudentNamePart(studentForm.familyName) || !normalizeStudentNamePart(studentForm.givenName)) {
      setBusy(false);
      setLoadError(`${tenantLabels.student}の姓と名を入力してください。`);
      return;
    }

    if (studentForm.familyAccountMode === "createNewFamilyAccount" && !studentForm.createFamilyAccountDisplayName.trim()) {
      setBusy(false);
      setLoadError(`新しい${tenantLabels.familyAccount}名を入力してください。`);
      return;
    }

    if (studentForm.familyAccountMode === "useExistingFamilyAccount" && !studentForm.familyAccountId) {
      setBusy(false);
      setLoadError(`既存の${tenantLabels.familyAccount}を選択してください。`);
      return;
    }

    try {
      const created = await createAdminStudent(tenantSlug, buildCreateStudentRequest(studentForm));
      const message = `${tenantLabels.student}を作成しました: ${created.displayName}`;
      setFeedbackMessage(message);
      navigate(`/t/${tenantSlug}/teacher/admin/students/${created.studentId}`, {
        replace: true,
        state: { adminFeedbackMessage: message }
      });
    } catch (error) {
      if (isUnauthenticatedApiError(error)) {
        navigate(`/t/${tenantSlug}/entry`, { replace: true });
        return;
      }

      setLoadError(getProtectedApiErrorMessage(error, `${tenantLabels.student}の作成に失敗しました`));
    } finally {
      setBusy(false);
    }
  }

  return (
    <OperatorPageScaffold
      currentArea="admin"
      tenantSlug={tenantSlug}
      tenantName={session?.tenant.displayName}
      session={session}
      title={`${tenantLabels.student}登録`}
      description={`${tenantLabels.student}を登録します。${tenantLabels.familyAccount}は同時作成・既存利用・未設定を選べます。`}
      links={buildAdminRosterLinks(tenantLabels)}
    >
      <div style={{ display: "grid", gap: 16 }}>
        {routeStatus === "checking" || sessionStatus === "loading" ? <p>セッションを確認しています。</p> : null}
        {routeStatus === "redirecting" ? <p>利用開始へ移動しています。</p> : null}
        {sessionStatus === "error" ? <p>セッション確認に失敗しました。{errorMessage}</p> : null}
        {routeStatus === "authenticated" && !hasAdminRole ? <p>この画面は管理者のみ利用できます。</p> : null}
        {loadState === "loading" ? <p>{tenantLabels.familyAccount}を読み込んでいます。</p> : null}
        {loadError ? <p>{loadError}</p> : null}
        {feedbackMessage ? <p>{feedbackMessage}</p> : null}
        {isSessionReady && hasAdminRole ? (
          <StudentCreateForm
            form={studentForm}
            familyAccounts={familyAccounts}
            tenantLabels={tenantLabels}
            busy={busy}
            onChange={setStudentForm}
            onEditCreateFamilyAccountName={() => setHasEditedCreateFamilyAccountDisplayName(true)}
            onSubmit={(event) => void handleCreateStudent(event)}
          />
        ) : null}
      </div>
    </OperatorPageScaffold>
  );
}
